import {useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useProjectsApi, useProjectsData} from '@/contexts/ProjectsContext';
import {invalidateProjectsList, projectsApi} from '@/features/projects';
import {queryKeys} from '@/lib/query-keys';
import {useToast} from '@/hooks/use-toast';
import {Button} from '@/shared/ui/button';
import {
  Home,
  ChevronRight,
  AlertTriangle,
  Upload,
  Download,
  CircleCheck,
} from 'lucide-react';
import {isProjectEmailTemplateConfigured} from '@/shared/services/api-client';
import {cn} from '@/lib/utils';
import {Input} from '@/shared/ui/input';
import EmailEditor, {type EmailEditorHandle} from '@/shared/ui/email-editor';
import {useProjectRole} from '@/hooks/use-project-role';
import {getProjectPermissionFlags} from '@/shared/lib/project-permissions';
import {useAuth} from '@/contexts/AuthContext';
import {isProjectEmailTemplatesEnabled} from '@/config/env';
import type {ProjectEmailTemplateConfig} from '@/shared/types/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/shared/ui/tabs';
import EmailTemplateHistoryPanel from './EmailTemplateHistoryPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';

type EmailTemplateKey =
  | 'booking_confirmation'
  | 'booking_confirmation_self_booking'
  | 'reschedule_confirmation'
  | 'cancellation_confirmation'
  | 'moderator_change'
  | 'session_completed'
  | 'reminder'
  | 'no_show'
  | 'self_booking_invite'
  | 'self_booking_reminder';

type TemplateConfig = {
  subject: string;
  body: string;
};

type EmailTemplateConfig = Record<EmailTemplateKey, TemplateConfig>;
type BulkUploadEntry = {
  template_key?: string;
  subject?: string;
  body?: string;
};

const TEMPLATE_TABS: Array<{id: EmailTemplateKey; label: string}> = [
  {id: 'booking_confirmation', label: 'Booking Confirmation'},
  {id: 'booking_confirmation_self_booking', label: 'Self Booking Confirmation'},
  {id: 'reschedule_confirmation', label: 'Reschedule'},
  {id: 'cancellation_confirmation', label: 'Cancel'},
  {id: 'moderator_change', label: 'Moderator Change'},
  {id: 'reminder', label: 'Reminder'},
  {id: 'session_completed', label: 'Session Completed'},
  {id: 'no_show', label: 'No Show'},
  {id: 'self_booking_invite', label: 'Self-Booking Invite'},
  {id: 'self_booking_reminder', label: 'Self-Booking Reminder'},
];

const TAB_HELP_TEXT: Record<EmailTemplateKey, string> = {
  booking_confirmation:
    'Sent to the participant when a session is booked on their behalf by the scheduling team. Cancel/reschedule links are intentionally omitted — the team manages changes.',
  booking_confirmation_self_booking:
    'Sent to the participant when they book a session via a self-booking link. Includes cancel and reschedule links so participants can manage their booking.',
  reschedule_confirmation:
    'Sent to participant when a session is rescheduled.',
  cancellation_confirmation:
    'Sent to participant when a session is cancelled.',
  moderator_change:
    'Sent to the participant when the moderator assigned to their session changes. Use default copy as a starting point, then Save. Reset restores the last saved version of this template.',
  session_completed:
    'Sent to the participant when a session is marked completed.',
  reminder:
    'Sent to participants about 24 hours before their session when the booking has reminders enabled.',
  no_show:
    'Sent to the participant when a session is marked as No Show.',
  self_booking_invite:
    'Sent to participants when a Scheduler sends a self-booking invite (single or bulk). Must include the {{self_booking_link}} placeholder so each participant receives their unique booking URL.',
  self_booking_reminder:
    'Sent to participants who received a self-booking invite but haven\'t booked yet, at the times configured below. Must include the {{self_booking_link}} placeholder.',
};

const SUBJECT_PLACEHOLDER_TEXT: Record<EmailTemplateKey, string> = {
  booking_confirmation:
    'Example: Your session is confirmed - {{session_type}} on {{session_date}}',
  booking_confirmation_self_booking:
    'Example: Your session is confirmed - {{session_type}} on {{session_date}}',
  reschedule_confirmation:
    'Example: Rescheduled – {{session_type}} (new: {{new_date_time}})',
  cancellation_confirmation:
    'Example: Your session was cancelled - {{session_type}} on {{session_date}}',
  moderator_change:
    'Moderator update for your {{session_type}} on {{session_date}}',
  session_completed:
    'Example: Thank you for completing your {{session_type}} session',
  reminder:
    'Example: Reminder: Your {{session_type}} is scheduled for tomorrow ({{session_date}})',
  no_show:
    'Example: Session marked as No Show – {{session_type}} on {{session_date}}',
  self_booking_invite:
    'Example: You\'re invited to book a session for {{project_name}} – link expires {{link_expiry_date}}',
  self_booking_reminder:
    'Example: Reminder — you\'re invited to book a session for {{project_name}} (Reminder {{reminder_number}} of {{total_reminders}})',
};

const COMMON_PLACEHOLDERS = [
  '{{participant_name}}',
  '{{project_name}}',
  '{{session_type}}',
  '{{session_date}}',
  '{{session_time}}',
  '{{session_duration}}',
  '{{session_location}}',
  '{{moderator_names}}',
];

const PLACEHOLDERS_BY_TEMPLATE: Record<EmailTemplateKey, string[]> = {
  booking_confirmation: [
    ...COMMON_PLACEHOLDERS,
  ],
  booking_confirmation_self_booking: [
    ...COMMON_PLACEHOLDERS,
    '{{participant_reschedule_url}}',
    '{{participant_cancel_url}}',
  ],
  reschedule_confirmation: [
    ...COMMON_PLACEHOLDERS,
    '{{previous_date_time}}',
    '{{new_date_time}}',
    '{{participant_reschedule_url}}',
    '{{participant_cancel_url}}',
  ],
  cancellation_confirmation: [
    ...COMMON_PLACEHOLDERS,
    '{{cancellation_reason}}',
  ],
  moderator_change: [
    '{{participant_name}}',
    '{{project_name}}',
    '{{session_type}}',
    '{{session_date}}',
    '{{session_time}}',
    '{{session_duration}}',
    '{{session_location}}',
    '{{new_moderator_names}}',
    '{{moderator_names}}',
    '{{previous_moderator_names}}',
    '{{removed_moderator_names}}',
  ],
  no_show: [
    ...COMMON_PLACEHOLDERS,
  ],
  session_completed: [
    '{{participant_name}}',
    '{{project_name}}',
    '{{session_type}}',
    '{{session_date}}',
    '{{session_time}}',
    '{{moderator_names}}',
  ],
  reminder: [
    ...COMMON_PLACEHOLDERS,
    '{{hours_before_session}}',
  ],
  self_booking_invite: [
    '{{participant_name}}',
    '{{project_name}}',
    '{{session_name}}',
    '{{session_duration}}',
    '{{self_booking_link}}',
    '{{link_expiry_date}}',
    // OD-1325 AC-15: the meeting point chosen for this invite batch. Booking
    // Confirmation has carried this since OD-820; the invite that tells someone
    // where to turn up did not.
    '{{session_location}}',
    '{{moderator_name}}',
  ],
  self_booking_reminder: [
    '{{participant_name}}',
    '{{project_name}}',
    '{{session_name}}',
    '{{session_duration}}',
    '{{self_booking_link}}',
    '{{link_expiry_date}}',
    // Resolves to the meeting point on the invite this reminder chases, so the
    // two messages can never name different venues (AC-16).
    '{{session_location}}',
    '{{reminder_number}}',
    '{{total_reminders}}',
  ],
};

const DEFAULT_MODERATOR_CHANGE_SUBJECT =
  'Moderator update for your {{session_type}} on {{session_date}}';

const DEFAULT_MODERATOR_CHANGE_BODY = `Hi {{participant_name}},

The moderator for your {{session_type}} session in {{project_name}} has changed.

Session details:
- Date: {{session_date}}
- Time: {{session_time}}
- Duration: {{session_duration}}
- Location: {{session_location}}

Previous moderator(s): {{previous_moderator_names}}
New moderator(s): {{new_moderator_names}}
Removed moderator(s): {{removed_moderator_names}}

If you have questions, please contact the project team.`;

const DEFAULT_BODY_PLACEHOLDER =
  'Enter body text. You can use variables like {{participant_name}}, {{project_name}}, {{session_date}}.';

const BODY_PLACEHOLDER_TEXT: Record<EmailTemplateKey, string> = {
  booking_confirmation: DEFAULT_BODY_PLACEHOLDER,
  booking_confirmation_self_booking: DEFAULT_BODY_PLACEHOLDER,
  reschedule_confirmation: DEFAULT_BODY_PLACEHOLDER,
  cancellation_confirmation: DEFAULT_BODY_PLACEHOLDER,
  moderator_change: DEFAULT_MODERATOR_CHANGE_BODY,
  session_completed: DEFAULT_BODY_PLACEHOLDER,
  reminder: DEFAULT_BODY_PLACEHOLDER,
  no_show: DEFAULT_BODY_PLACEHOLDER,
  self_booking_invite: DEFAULT_BODY_PLACEHOLDER,
  self_booking_reminder: DEFAULT_BODY_PLACEHOLDER,
};

const MANDATORY_PLACEHOLDERS_BY_TEMPLATE: Partial<
  Record<EmailTemplateKey, string[]>
> = {
  booking_confirmation_self_booking: [
    '{{participant_reschedule_url}}',
    '{{participant_cancel_url}}',
  ],
  self_booking_invite: [
    '{{self_booking_link}}',
    // Required, not merely available: an invite whose body omits the venue
    // sends the participant to an address they never receive. The backend
    // refuses the send for the same reason (OD-1325 edge case), so accepting it
    // here would only move the failure to the moment it matters.
    '{{session_location}}',
  ],
  self_booking_reminder: [
    '{{self_booking_link}}',
    '{{session_location}}',
  ],
};

const EMPTY_CONFIG: EmailTemplateConfig = {
  booking_confirmation: {subject: '', body: ''},
  booking_confirmation_self_booking: {subject: '', body: ''},
  reschedule_confirmation: {subject: '', body: ''},
  cancellation_confirmation: {subject: '', body: ''},
  moderator_change: {subject: '', body: ''},
  session_completed: {subject: '', body: ''},
  reminder: {subject: '', body: ''},
  no_show: {subject: '', body: ''},
  self_booking_invite: {subject: '', body: ''},
  self_booking_reminder: {subject: '', body: ''},
};

const MIN_PARTICIPANT_REMINDER_SLOTS = 1;
const MAX_PARTICIPANT_REMINDER_SLOTS = 3;
const DEFAULT_PARTICIPANT_REMINDER_HOURS = [24];
const MAX_REMINDER_HOURS_VALUE = 8760;

const INLINE_PLACEHOLDER_REGEX = /(\{\{[a-zA-Z0-9_]+\}\})/g;
const PLACEHOLDER_REGEX = /\{\{[a-zA-Z0-9_]+\}\}/g;

// Reminder-hour rows are reorderable/removable, so each needs a stable identity
// rather than its array index as a React key.
type ReminderHourSlot = {id: string; value: string};
let reminderHourSlotSeq = 0;
function makeReminderHourSlot(value: string): ReminderHourSlot {
  reminderHourSlotSeq += 1;
  return {id: `reminder-slot-${reminderHourSlotSeq}`, value};
}

type ReminderHoursCommit = {
  valid: boolean;
  hasDuplicates: boolean;
  hours: number[];
  hasAtLeastOne: boolean;
};

/** Parse/validate the draft reminder-hour rows into a committable result. */
function computeReminderHoursCommit(
  slots: ReminderHourSlot[]
): ReminderHoursCommit {
  const parsed: number[] = [];
  for (const slot of slots) {
    const trimmed = slot.value.trim();
    if (trimmed === '') continue;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 1 || n > MAX_REMINDER_HOURS_VALUE) {
      return {valid: false, hasDuplicates: false, hours: [], hasAtLeastOne: false};
    }
    parsed.push(n);
  }
  if (parsed.length !== new Set(parsed).size) {
    return {
      valid: false,
      hasDuplicates: true,
      hours: [],
      hasAtLeastOne: parsed.length >= MIN_PARTICIPANT_REMINDER_SLOTS,
    };
  }
  const hours = [...parsed]
    .sort((a, b) => b - a)
    .slice(0, MAX_PARTICIPANT_REMINDER_SLOTS);
  return {
    valid: true,
    hasDuplicates: false,
    hours,
    hasAtLeastOne: hours.length >= MIN_PARTICIPANT_REMINDER_SLOTS,
  };
}

type BulkUploadValidationResult = {
  validTemplates: Partial<EmailTemplateConfig>;
  validTemplateKeys: EmailTemplateKey[];
  errors: string[];
  warnings: string[];
};
function normalizeParticipantReminderHours(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [...DEFAULT_PARTICIPANT_REMINDER_HOURS];
  const parsed: number[] = [];
  for (const item of raw) {
    const n = typeof item === 'number' ? item : parseInt(String(item), 10);
    if (Number.isFinite(n) && n >= 1 && n <= MAX_REMINDER_HOURS_VALUE) {
      parsed.push(Math.floor(n));
    }
  }
  const deduped = [...new Set(parsed)];
  if (deduped.length === 0) return [...DEFAULT_PARTICIPANT_REMINDER_HOURS];
  return deduped.sort((a, b) => b - a).slice(0, MAX_PARTICIPANT_REMINDER_SLOTS);
}

function formatReminderHoursSummary(hours: number[]): string {
  if (hours.length === 0) return 'No reminders scheduled';
  const sorted = [...hours].sort((a, b) => b - a);
  return `${sorted.map((h) => `${h}h`).join(', ')} before session`;
}

/** Same multiset of hours (order-independent). */
function areParticipantReminderHoursEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((n, i) => n === sb[i]);
}

type SelfBookingReminderOffsetType = 'after_sent' | 'before_expiry';
const MAX_SELF_BOOKING_REMINDER_SLOTS = 3;
const MAX_SELF_BOOKING_REMINDER_HOURS = 8760;

// Offset rows are reorderable/removable, so each needs a stable identity
// rather than its array index as a React key.
type SelfBookingReminderOffsetRow = {
  id: string;
  type: SelfBookingReminderOffsetType;
  hours: string;
};
let selfBookingReminderOffsetSeq = 0;
function makeSelfBookingReminderOffsetRow(
  type: SelfBookingReminderOffsetType,
  hours: string
): SelfBookingReminderOffsetRow {
  selfBookingReminderOffsetSeq += 1;
  return {id: `self-booking-reminder-offset-${selfBookingReminderOffsetSeq}`, type, hours};
}

type NormalizedSelfBookingReminderOffset = {
  type: SelfBookingReminderOffsetType;
  hours: number;
};

/** Validate + dedupe + cap raw offset rows/config entries. */
function normalizeSelfBookingReminderOffsets(
  raw: unknown
): NormalizedSelfBookingReminderOffset[] {
  if (!Array.isArray(raw)) return [];
  const parsed: NormalizedSelfBookingReminderOffset[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const type = (item as {type?: unknown}).type;
    const hoursRaw = (item as {hours?: unknown}).hours;
    const n = typeof hoursRaw === 'number' ? hoursRaw : parseInt(String(hoursRaw), 10);
    if (
      (type !== 'after_sent' && type !== 'before_expiry') ||
      !Number.isFinite(n) ||
      n < 1 ||
      n > MAX_SELF_BOOKING_REMINDER_HOURS
    ) {
      continue;
    }
    parsed.push({type, hours: Math.floor(n)});
  }
  const seen = new Set<string>();
  const deduped: NormalizedSelfBookingReminderOffset[] = [];
  for (const offset of parsed) {
    const key = `${offset.type}:${offset.hours}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(offset);
  }
  return deduped.slice(0, MAX_SELF_BOOKING_REMINDER_SLOTS);
}

function areSelfBookingReminderOffsetsEqual(
  a: NormalizedSelfBookingReminderOffset[],
  b: NormalizedSelfBookingReminderOffset[]
): boolean {
  if (a.length !== b.length) return false;
  const key = (o: NormalizedSelfBookingReminderOffset) => `${o.type}:${o.hours}`;
  const sa = [...a].map(key).sort((x, y) => x.localeCompare(y));
  const sb = [...b].map(key).sort((x, y) => x.localeCompare(y));
  return sa.every((v, i) => v === sb[i]);
}

const ORDINAL_WORDS = ['first', 'second', 'third'];

/** "PM can preview reminder configuration" — a plain-language summary. */
function formatSelfBookingReminderPreview(
  offsets: NormalizedSelfBookingReminderOffset[]
): string {
  if (offsets.length === 0) return 'No reminders will be sent.';
  const parts = offsets.map((o, i) => {
    const ordinal = ORDINAL_WORDS[i] ?? `${i + 1}th`;
    const timing =
      o.type === 'before_expiry'
        ? `${o.hours} hour${o.hours === 1 ? '' : 's'} before the invitation expires`
        : `${o.hours} hour${o.hours === 1 ? '' : 's'} after the invite is sent`;
    return `${ordinal} at ${timing}`;
  });
  return `Participants will receive ${offsets.length} reminder${offsets.length > 1 ? 's' : ''}: ${parts.join(', ')}.`;
}

type SelfBookingReminderOffsetsCommit = {
  valid: boolean;
  hasDuplicates: boolean;
  offsets: NormalizedSelfBookingReminderOffset[];
};

/**
 * Parse/validate the draft self-booking-reminder rows into a committable
 * result. Unlike session reminders, zero valid offsets is a legitimate,
 * deliberate state — it simply means reminders are off for this project.
 */
function computeSelfBookingReminderOffsetsCommit(
  rows: SelfBookingReminderOffsetRow[]
): SelfBookingReminderOffsetsCommit {
  const parsed: NormalizedSelfBookingReminderOffset[] = [];
  for (const row of rows) {
    const trimmed = row.hours.trim();
    if (trimmed === '') continue;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 1 || n > MAX_SELF_BOOKING_REMINDER_HOURS) {
      return {valid: false, hasDuplicates: false, offsets: []};
    }
    parsed.push({type: row.type, hours: n});
  }
  const keys = parsed.map((o) => `${o.type}:${o.hours}`);
  if (keys.length !== new Set(keys).size) {
    return {valid: false, hasDuplicates: true, offsets: []};
  }
  const offsets = parsed.slice(0, MAX_SELF_BOOKING_REMINDER_SLOTS);
  return {valid: true, hasDuplicates: false, offsets};
}

function buildEmailTemplatePayload(
  templates: EmailTemplateConfig,
  existing: ProjectEmailTemplateConfig | null | undefined,
  participantReminderHours: number[] | undefined,
  includeReminderHours: boolean,
  selfBookingReminderOffsets: NormalizedSelfBookingReminderOffset[]
): ProjectEmailTemplateConfig {
  const base: ProjectEmailTemplateConfig = {
    ...(existing || {}),
    booking_confirmation: templates.booking_confirmation,
    booking_confirmation_self_booking: templates.booking_confirmation_self_booking,
    reschedule_confirmation: templates.reschedule_confirmation,
    cancellation_confirmation: templates.cancellation_confirmation,
    moderator_change: templates.moderator_change,
    session_completed: templates.session_completed,
    reminder: templates.reminder,
    no_show: templates.no_show,
    self_booking_invite: templates.self_booking_invite,
    self_booking_reminder: templates.self_booking_reminder,
  };
  if (includeReminderHours && participantReminderHours !== undefined) {
    base.participant_reminder_hours = [...participantReminderHours];
  }
  base.self_booking_reminder_offsets = selfBookingReminderOffsets.map((o) => ({...o}));
  return base;
}

function normalizeConfig(raw: unknown): EmailTemplateConfig {
  const cfg = (raw || {}) as Record<string, {subject?: string; body?: string}>;
  const next: EmailTemplateConfig = {...EMPTY_CONFIG};
  for (const tab of TEMPLATE_TABS) {
    next[tab.id] = {
      subject: cfg?.[tab.id]?.subject || '',
      body: cfg?.[tab.id]?.body || '',
    };
  }
  return next;
}

function buildBulkUploadTemplateDocument(): string {
  const templates = TEMPLATE_TABS.map((tab) => ({
    template_key: tab.id,
    label: tab.label,
    subject: '',
    body: '',
  }));
  return JSON.stringify(
    {
      version: 1,
      description:
        'Fill subject/body for each template. Keep template_key unchanged.',
      templates,
    },
    null,
    2
  );
}

type BulkUploadRowResult =
  | {kind: 'error'; message: string}
  | {kind: 'warning'; message: string}
  | {kind: 'ok'; key: EmailTemplateKey; template: TemplateConfig};

/** Self-booking templates must keep the reschedule/cancel links; returns an error message or null. */
function validateSelfBookingTokens(
  key: EmailTemplateKey,
  body: string
): string | null {
  if (key === 'booking_confirmation_self_booking') {
    const missing: string[] = [];
    if (!body.includes('{{participant_reschedule_url}}')) {
      missing.push('{{participant_reschedule_url}}');
    }
    if (!body.includes('{{participant_cancel_url}}')) {
      missing.push('{{participant_cancel_url}}');
    }
    if (missing.length === 0) return null;
    return `Template "${key}": body must include ${missing.join(' and ')} so participants can manage their booking.`;
  }
  if (key === 'self_booking_invite' || key === 'self_booking_reminder') {
    if (!body.includes('{{self_booking_link}}')) {
      return `Template "${key}": body must include {{self_booking_link}} — this is the unique URL each participant uses to book their session.`;
    }
    // OD-1325 AC-15. A session type can now hold several meeting points, so the
    // venue differs per invite batch and cannot be written into the copy by
    // hand. Without the placeholder the participant is told when to attend but
    // never where.
    if (!body.includes('{{session_location}}')) {
      return `Template "${key}": body must include {{session_location}} — each invite can be sent for a different meeting point, so the address cannot be typed into the template.`;
    }
  }
  return null;
}

/** Validate a single bulk-upload row into an error, warning, or accepted template. */
function validateBulkUploadRow(
  row: BulkUploadEntry | undefined,
  index: number,
  expectedKeys: Set<EmailTemplateKey>,
  allowedByTemplate: Record<EmailTemplateKey, Set<string>>
): BulkUploadRowResult {
  const keyRaw = (row?.template_key || '').trim().toLowerCase();
  if (!keyRaw) {
    return {kind: 'error', message: `Row ${index + 1}: template_key is required.`};
  }
  if (!expectedKeys.has(keyRaw as EmailTemplateKey)) {
    return {
      kind: 'warning',
      message: `Row ${index + 1}: ignored unknown template_key "${keyRaw}" (extensible slot).`,
    };
  }
  const key = keyRaw as EmailTemplateKey;
  const subject = typeof row?.subject === 'string' ? row.subject.trim() : '';
  const body = typeof row?.body === 'string' ? row.body.trim() : '';

  if (!subject) {
    return {kind: 'error', message: `Template "${key}": subject is empty.`};
  }
  if (!body) {
    return {kind: 'error', message: `Template "${key}": body is empty.`};
  }

  const unknownTokens = [
    ...new Set(`${subject}\n${body}`.match(PLACEHOLDER_REGEX) || []),
  ].filter((token) => !allowedByTemplate[key].has(token));
  if (unknownTokens.length > 0) {
    return {
      kind: 'error',
      message: `Template "${key}": unrecognized placeholder(s): ${unknownTokens.join(', ')}.`,
    };
  }

  const selfBookingError = validateSelfBookingTokens(key, body);
  if (selfBookingError) {
    return {kind: 'error', message: selfBookingError};
  }

  return {kind: 'ok', key, template: {subject, body}};
}

function buildAllowedTokensByTemplate(): Record<EmailTemplateKey, Set<string>> {
  return {
    booking_confirmation: new Set(PLACEHOLDERS_BY_TEMPLATE.booking_confirmation),
    booking_confirmation_self_booking: new Set(PLACEHOLDERS_BY_TEMPLATE.booking_confirmation_self_booking),
    reschedule_confirmation: new Set(PLACEHOLDERS_BY_TEMPLATE.reschedule_confirmation),
    cancellation_confirmation: new Set(PLACEHOLDERS_BY_TEMPLATE.cancellation_confirmation),
    moderator_change: new Set(PLACEHOLDERS_BY_TEMPLATE.moderator_change),
    session_completed: new Set(PLACEHOLDERS_BY_TEMPLATE.session_completed),
    reminder: new Set(PLACEHOLDERS_BY_TEMPLATE.reminder),
    no_show: new Set(PLACEHOLDERS_BY_TEMPLATE.no_show),
    self_booking_invite: new Set(PLACEHOLDERS_BY_TEMPLATE.self_booking_invite),
    self_booking_reminder: new Set(PLACEHOLDERS_BY_TEMPLATE.self_booking_reminder),
  };
}

function validateBulkUploadDocument(raw: unknown): BulkUploadValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const validTemplates: Partial<EmailTemplateConfig> = {};
  const validTemplateKeys: EmailTemplateKey[] = [];
  const expectedKeys = new Set<EmailTemplateKey>(TEMPLATE_TABS.map((t) => t.id));
  const allowedByTemplate = buildAllowedTokensByTemplate();

  const payload = raw as {templates?: BulkUploadEntry[]};
  if (!payload || !Array.isArray(payload.templates)) {
    return {
      validTemplates,
      validTemplateKeys,
      errors: [
        'Invalid file format. Expected JSON object with a "templates" array.',
      ],
      warnings,
    };
  }

  for (const [index, row] of payload.templates.entries()) {
    const result = validateBulkUploadRow(row, index, expectedKeys, allowedByTemplate);
    if (result.kind === 'error') {
      errors.push(result.message);
    } else if (result.kind === 'warning') {
      warnings.push(result.message);
    } else {
      validTemplates[result.key] = result.template;
      validTemplateKeys.push(result.key);
    }
  }

  for (const required of expectedKeys) {
    const hasRow = payload.templates.some(
      (row) => (row?.template_key || '').trim().toLowerCase() === required
    );
    if (!validTemplateKeys.includes(required) && !hasRow) {
      errors.push(`Missing required template "${required}".`);
    }
  }

  return {
    validTemplates,
    validTemplateKeys,
    errors,
    warnings,
  };
}

type BulkUploadReport = {
  applied: EmailTemplateKey[];
  errors: string[];
  warnings: string[];
};

function BulkUploadReportPanel({report}: Readonly<{report: BulkUploadReport | null}>) {
  if (!report) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
      <p className="text-slate-700">
        Imported:{' '}
        {report.applied.length > 0 ? report.applied.join(', ') : 'none'}
      </p>
      {report.errors.length > 0 && (
        <div>
          <p className="font-medium text-destructive">Errors</p>
          <ul className="list-disc pl-4 text-destructive space-y-1">
            {report.errors.map((err, idx) => (
              <li key={`${err}-${idx}`}>{err}</li>
            ))}
          </ul>
        </div>
      )}
      {report.warnings.length > 0 && (
        <div>
          <p className="font-medium text-amber-700">Warnings</p>
          <ul className="list-disc pl-4 text-amber-700 space-y-1">
            {report.warnings.map((warning, idx) => (
              <li key={`${warning}-${idx}`}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function isTemplateConfigValid(
  _key: EmailTemplateKey,
  template: TemplateConfig | undefined
): boolean {
  if (!template) return false;
  const subject = (template.subject || '').trim();
  const body = (template.body || '').trim();
  return !!subject && !!body;
}

// Self-booking confirmation must give participants self-service control — if
// the PM has started filling it in, both cancel and reschedule links are
// required. (An entirely empty template just means "not yet configured" and
// is gated separately by the per-flow preflight on the booking-links route.)
function computeSelfBookingMissingTokens(
  template: TemplateConfig | undefined
): string[] {
  const subject = (template?.subject || '').trim();
  const body = template?.body || '';
  const hasContent = subject !== '' || body.trim() !== '';
  if (!hasContent) return [];
  const required = ['{{participant_reschedule_url}}', '{{participant_cancel_url}}'];
  return required.filter((token) => !body.includes(token));
}

// Self-booking invite and reminder must both contain the booking link so
// participants can actually book.
function computeSelfBookingLinkMissingTokens(
  template: TemplateConfig | undefined
): string[] {
  const body = template?.body || '';
  if (!body.trim()) return [];
  return body.includes('{{self_booking_link}}') ? [] : ['{{self_booking_link}}'];
}

/**
 * @param embedded true when rendered inside the Project Setup tabs, which
 * already provide the breadcrumb and page title — rendering our own would
 * duplicate them.
 */
export default function ProjectEmailSetup({embedded = false}: {embedded?: boolean} = {}) {
  const {
    state: {currentProject},
  } = useProjectsData();
  const {updateProject, refreshCurrentProject} = useProjectsApi();
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const {user} = useAuth();
  const [activeTab, setActiveTab] =
    useState<EmailTemplateKey>('booking_confirmation');
  const [config, setConfig] = useState<EmailTemplateConfig>(EMPTY_CONFIG);
  const [savedConfig, setSavedConfig] = useState<EmailTemplateConfig>(EMPTY_CONFIG);
  const [participantReminderHours, setParticipantReminderHours] = useState<
    number[]
  >([...DEFAULT_PARTICIPANT_REMINDER_HOURS]);
  const [savedParticipantReminderHours, setSavedParticipantReminderHours] =
    useState<number[]>([...DEFAULT_PARTICIPANT_REMINDER_HOURS]);
  const [reminderHoursModalOpen, setReminderHoursModalOpen] = useState(false);
  const [draftReminderHourInputs, setDraftReminderHourInputs] = useState<
    ReminderHourSlot[]
  >(() => [makeReminderHourSlot('24')]);
  const [selfBookingReminderOffsetRows, setSelfBookingReminderOffsetRows] = useState<
    SelfBookingReminderOffsetRow[]
  >([]);
  const [savedSelfBookingReminderOffsets, setSavedSelfBookingReminderOffsets] =
    useState<NormalizedSelfBookingReminderOffset[]>([]);
  const [selfBookingReminderHoursModalOpen, setSelfBookingReminderHoursModalOpen] =
    useState(false);
  const [draftSelfBookingReminderOffsetRows, setDraftSelfBookingReminderOffsetRows] =
    useState<SelfBookingReminderOffsetRow[]>([]);
  const [saving, setSaving] = useState(false);

  const reminderFeatureEnabled = isProjectEmailTemplatesEnabled();
  const [activeEditorField, setActiveEditorField] = useState<'subject' | 'body'>(
    'body'
  );
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const emailEditorRef = useRef<EmailEditorHandle | null>(null);
  const [subjectScrollLeft, setSubjectScrollLeft] = useState(0);
  const [saveTooltipOpen, setSaveTooltipOpen] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkUploadReport, setBulkUploadReport] = useState<{
    applied: EmailTemplateKey[];
    errors: string[];
    warnings: string[];
  } | null>(null);
  const bulkUploadInputRef = useRef<HTMLInputElement | null>(null);

  const {role: projectRole, loading: roleLoading} = useProjectRole(
    currentProject?.id,
    currentProject
      ? {
          created_by: currentProject.created_by,
          user_role: currentProject.user_role || undefined,
          user_role_source: currentProject.user_role_source || undefined,
        }
      : undefined
  );
  const {isAdmin, isProjectManager, isScheduler} = getProjectPermissionFlags({
    projectRole,
    roleLoading,
    userRole: user?.role,
    userEmail: user?.email,
  });
  const canEdit = isAdmin || isProjectManager || isScheduler;

  useEffect(() => {
    const raw = currentProject?.email_template_config;
    const normalized = normalizeConfig(raw);
    setConfig(normalized);
    setSavedConfig(normalized);
    const hours = normalizeParticipantReminderHours(
      (raw as ProjectEmailTemplateConfig | undefined)?.participant_reminder_hours
    );
    setParticipantReminderHours(hours);
    setSavedParticipantReminderHours(hours);
    const rawCfg = raw as ProjectEmailTemplateConfig | undefined;
    const reminderOffsets = normalizeSelfBookingReminderOffsets(
      rawCfg?.self_booking_reminder_offsets
    );
    setSelfBookingReminderOffsetRows(
      reminderOffsets.map((o) => makeSelfBookingReminderOffsetRow(o.type, String(o.hours)))
    );
    setSavedSelfBookingReminderOffsets(reminderOffsets);
  }, [currentProject?.id, currentProject?.email_template_config]);

  useEffect(() => {
    setSubjectScrollLeft(0);
    if (subjectRef.current) subjectRef.current.scrollLeft = 0;
  }, [activeTab]);

  const activeConfig = useMemo(() => config[activeTab], [config, activeTab]);
  const selfBookingReminderOffsetsCommit = useMemo(
    () =>
      normalizeSelfBookingReminderOffsets(
        selfBookingReminderOffsetRows.map((r) => ({type: r.type, hours: r.hours}))
      ),
    [selfBookingReminderOffsetRows]
  );
  const hasChanges = useMemo(() => {
    const templatesDirty =
      JSON.stringify(config) !== JSON.stringify(savedConfig);
    const selfBookingReminderDirty = !areSelfBookingReminderOffsetsEqual(
      selfBookingReminderOffsetsCommit,
      savedSelfBookingReminderOffsets
    );
    if (!reminderFeatureEnabled) return templatesDirty || selfBookingReminderDirty;
    const hoursDirty =
      JSON.stringify(participantReminderHours) !==
      JSON.stringify(savedParticipantReminderHours);
    return templatesDirty || hoursDirty || selfBookingReminderDirty;
  }, [
    config,
    savedConfig,
    reminderFeatureEnabled,
    participantReminderHours,
    savedParticipantReminderHours,
    selfBookingReminderOffsetsCommit,
    savedSelfBookingReminderOffsets,
  ]);

  const hasAtLeastOneParticipantReminderHour =
    participantReminderHours.length >= MIN_PARTICIPANT_REMINDER_SLOTS;

  const selfBookingMissingTokens = computeSelfBookingMissingTokens(
    config.booking_confirmation_self_booking
  );
  const selfBookingUrlsMissing = selfBookingMissingTokens.length > 0;

  const selfBookingInviteMissingTokens = computeSelfBookingLinkMissingTokens(
    config.self_booking_invite
  );
  const selfBookingInviteLinkMissing = selfBookingInviteMissingTokens.length > 0;

  const selfBookingReminderMissingTokens = computeSelfBookingLinkMissingTokens(
    config.self_booking_reminder
  );
  const selfBookingReminderLinkMissing = selfBookingReminderMissingTokens.length > 0;

  const canSaveEmailSetup =
    canEdit &&
    hasChanges &&
    (!reminderFeatureEnabled || hasAtLeastOneParticipantReminderHour) &&
    !selfBookingUrlsMissing &&
    !selfBookingInviteLinkMissing &&
    !selfBookingReminderLinkMissing;
  const activePlaceholders = useMemo(
    () => PLACEHOLDERS_BY_TEMPLATE[activeTab] || COMMON_PLACEHOLDERS,
    [activeTab]
  );
  const activeMandatoryPlaceholders = useMemo(
    () => new Set(MANDATORY_PLACEHOLDERS_BY_TEMPLATE[activeTab] ?? []),
    [activeTab]
  );
  const missingMandatoryTokens = useMemo(
    () =>
      (MANDATORY_PLACEHOLDERS_BY_TEMPLATE[activeTab] ?? []).filter(
        (token) => !activeConfig.body.includes(token)
      ),
    [activeTab, activeConfig.body]
  );
  const templateValidityByKey = useMemo(() => {
    const result = {} as Record<EmailTemplateKey, boolean>;
    for (const tab of TEMPLATE_TABS) {
      result[tab.id] = isTemplateConfigValid(tab.id, config[tab.id]);
    }
    return result;
  }, [config]);
  const configuredTemplateCount = useMemo(
    () => TEMPLATE_TABS.filter((tab) => templateValidityByKey[tab.id]).length,
    [templateValidityByKey]
  );

  const updateActive = (next: Partial<TemplateConfig>) => {
    setConfig((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        ...next,
      },
    }));
  };

  const insertPlaceholder = (token: string) => {
    const subjectEl = subjectRef.current;
    // Body is the rich editor; subject is a plain input. Pick the field the user
    // last worked in (or the focused subject input).
    const field: 'subject' | 'body' =
      subjectEl && document.activeElement === subjectEl
        ? 'subject'
        : activeEditorField;

    // Rich body editor inserts the merge variable at its own caret.
    if (field === 'body') {
      emailEditorRef.current?.insertVariable(token);
      return;
    }

    // Subject (plain input) — splice the token in at the caret.
    const el = subjectEl;
    let newCaret = 0;
    setConfig((prev) => {
      const tab = activeTab;
      const valueStr = prev[tab].subject || '';
      let start = valueStr.length;
      let end = valueStr.length;
      if (el && document.activeElement === el) {
        start = el.selectionStart ?? 0;
        end = el.selectionEnd ?? 0;
      }
      const before = valueStr.slice(0, start);
      const prefix = start > 0 && valueStr[start - 1] !== ' ' ? ' ' : '';
      const insertion = `${prefix}${token} `;
      newCaret = start + insertion.length;
      return {
        ...prev,
        [tab]: {...prev[tab], subject: before + insertion + valueStr.slice(end)},
      };
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const t = subjectRef.current;
        if (t) {
          t.focus();
          const pos = Math.min(newCaret, t.value.length);
          t.setSelectionRange(pos, pos);
        }
      });
    });
  };

  const handleSubjectKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    if (start !== end || start === 0) return;

    const value = activeConfig.subject || '';
    const uptoCursor = value.slice(0, start);
    const match = uptoCursor.match(/(\{\{[a-zA-Z0-9_]+\}\})\s?$/);
    if (!match) return;

    const tokenWithWhitespace = match[0];
    const tokenStart = start - tokenWithWhitespace.length;
    const nextValue = value.slice(0, tokenStart) + value.slice(start);
    e.preventDefault();
    updateActive({subject: nextValue});

    requestAnimationFrame(() => {
      if (!subjectRef.current) return;
      subjectRef.current.selectionStart = tokenStart;
      subjectRef.current.selectionEnd = tokenStart;
    });
  };

  const renderHighlightedTokens = (
    value: string,
    mandatoryTokens: Set<string>
  ) => {
    if (!value) return null;
    const parts = value.split(INLINE_PLACEHOLDER_REGEX);
    return parts.map((part, index) => {
      if (!part) return null;
      if (part.match(/^\{\{[a-zA-Z0-9_]+\}\}$/)) {
        const isMandatory = mandatoryTokens.has(part);
        return (
          <span
            key={`${part}-${index}`}
            className="rounded bg-slate-100 text-slate-700"
            style={
              isMandatory
                ? {textShadow: '0.4px 0 currentColor, -0.4px 0 currentColor'}
                : undefined
            }
          >
            {part}
          </span>
        );
      }
      return <span key={`text-${index}`}>{part}</span>;
    });
  };

  const persistEmailTemplates = async (
    nextConfig: EmailTemplateConfig,
    successMessage: string,
    bulkUploadCount?: number
  ) => {
    if (!currentProject?.id) return;
    if (reminderFeatureEnabled && !hasAtLeastOneParticipantReminderHour) {
      toast({
        title: 'Reminder times required',
        description:
          'Add at least one participant reminder time (hours before the session) before saving.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setSaving(true);
      const existing = currentProject?.email_template_config as
        | ProjectEmailTemplateConfig
        | undefined;
      const email_template_config = buildEmailTemplatePayload(
        nextConfig,
        existing,
        normalizeParticipantReminderHours(participantReminderHours),
        reminderFeatureEnabled,
        selfBookingReminderOffsetsCommit
      );
      const response = await projectsApi.updateProject(currentProject.id, {
        email_template_config,
        ...(bulkUploadCount != null
          ? {email_template_bulk_upload_count: bulkUploadCount}
          : {}),
      });
      const updated = (response as any)?.data?.data || (response as any)?.data;
      if (updated) {
        updateProject(updated);
        const rawUp = (updated as any)?.email_template_config;
        const normalizedUpdated = normalizeConfig(rawUp);
        setConfig(normalizedUpdated);
        setSavedConfig(normalizedUpdated);
        const hoursSaved = normalizeParticipantReminderHours(
          (rawUp as ProjectEmailTemplateConfig | undefined)
            ?.participant_reminder_hours
        );
        setParticipantReminderHours(hoursSaved);
        setSavedParticipantReminderHours(hoursSaved);
        const rawUpCfg = rawUp as ProjectEmailTemplateConfig | undefined;
        const reminderOffsetsSaved = normalizeSelfBookingReminderOffsets(
          rawUpCfg?.self_booking_reminder_offsets
        );
        setSelfBookingReminderOffsetRows(
          reminderOffsetsSaved.map((o) => makeSelfBookingReminderOffsetRow(o.type, String(o.hours)))
        );
        setSavedSelfBookingReminderOffsets(reminderOffsetsSaved);
      }
      await refreshCurrentProject(currentProject.id);
      queryClient.invalidateQueries({queryKey: queryKeys.projects.detail(currentProject.id)});
      // The cached /projects list still holds the pre-save email_template_config,
      // and it gets re-broadcast into context on every project/tab switch
      // (Navigation.tsx's list-sync effect re-runs on location.search changes and
      // replaces the whole projects array). Without this refresh, leaving the
      // project and returning rebuilds currentProject from that stale snapshot and
      // the saved templates render as unconfigured.
      //
      // Both cache layers must be cleared: the localStorage copy sits
      // *underneath* TanStack Query, so invalidating alone would just make the
      // refetch re-read the same stale list from disk without calling the API.
      void invalidateProjectsList(queryClient);
      toast({
        title: 'Success',
        description: successMessage,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.error ||
          error?.message ||
          'Failed to save email template',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await persistEmailTemplates(config, 'Email template saved.');
  };

  const handleDownloadBulkTemplate = () => {
    const blob = new Blob([buildBulkUploadTemplateDocument()], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'email-templates-bulk-upload-template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkUploadFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!canEdit || !currentProject?.id) return;
    try {
      setBulkImporting(true);
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        toast({
          title: 'Invalid JSON',
          description: 'Upload a valid JSON document.',
          variant: 'destructive',
        });
        return;
      }

      const result = validateBulkUploadDocument(parsed);
      setBulkUploadReport({
        applied: result.validTemplateKeys,
        errors: result.errors,
        warnings: result.warnings,
      });

      if (result.validTemplateKeys.length === 0) {
        toast({
          title: 'No valid templates found',
          description:
            'Fix the reported issues and upload again. No templates were saved.',
          variant: 'destructive',
        });
        return;
      }

      const nextConfig: EmailTemplateConfig = {
        ...config,
        ...result.validTemplates,
      };
      await persistEmailTemplates(
        nextConfig,
        `Imported ${result.validTemplateKeys.length} template(s).`,
        result.validTemplateKeys.length
      );
      if (result.errors.length > 0 || result.warnings.length > 0) {
        toast({
          title: 'Import completed with issues',
          description:
            'Valid templates were saved. Review the import report for skipped templates.',
        });
      }
    } finally {
      setBulkImporting(false);
    }
  };

  const handleReset = () => {
    setConfig(savedConfig);
    if (reminderFeatureEnabled) {
      setParticipantReminderHours([...savedParticipantReminderHours]);
    }
    setSelfBookingReminderOffsetRows(
      savedSelfBookingReminderOffsets.map((o) =>
        makeSelfBookingReminderOffsetRow(o.type, String(o.hours))
      )
    );
  };

  const openSelfBookingReminderHoursModal = () => {
    setDraftSelfBookingReminderOffsetRows(
      selfBookingReminderOffsetRows.length > 0
        ? selfBookingReminderOffsetRows.map((r) =>
            makeSelfBookingReminderOffsetRow(r.type, r.hours)
          )
        : [makeSelfBookingReminderOffsetRow('after_sent', '')]
    );
    setSelfBookingReminderHoursModalOpen(true);
  };

  const handleSelfBookingReminderHoursModalCancel = () => {
    setSelfBookingReminderHoursModalOpen(false);
  };

  const handleSelfBookingReminderHoursModalSave = () => {
    const commit = computeSelfBookingReminderOffsetsCommit(
      draftSelfBookingReminderOffsetRows
    );
    if (commit.hasDuplicates) {
      toast({
        title: 'Duplicate reminders',
        description:
          'Two reminders have the same timing and type. Use a different number of hours or type for each row.',
        variant: 'destructive',
      });
      return;
    }
    if (!commit.valid) {
      toast({
        title: 'Invalid hours',
        description: `Enter whole hours between 1 and ${MAX_SELF_BOOKING_REMINDER_HOURS} for each reminder.`,
        variant: 'destructive',
      });
      return;
    }
    // Zero offsets is a valid, deliberate state — it means reminders are off.
    setSelfBookingReminderOffsetRows(
      commit.offsets.map((o) => makeSelfBookingReminderOffsetRow(o.type, String(o.hours)))
    );
    setSelfBookingReminderHoursModalOpen(false);
  };

  const addDraftSelfBookingReminderOffsetRow = () => {
    if (draftSelfBookingReminderOffsetRows.length >= MAX_SELF_BOOKING_REMINDER_SLOTS) return;
    setDraftSelfBookingReminderOffsetRows((prev) => [
      ...prev,
      makeSelfBookingReminderOffsetRow('after_sent', ''),
    ]);
  };

  const removeDraftSelfBookingReminderOffsetRow = (id: string) => {
    setDraftSelfBookingReminderOffsetRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateDraftSelfBookingReminderOffsetRow = (
    id: string,
    patch: Partial<Pick<SelfBookingReminderOffsetRow, 'type' | 'hours'>>
  ) => {
    setDraftSelfBookingReminderOffsetRows((prev) =>
      prev.map((r) => (r.id === id ? {...r, ...patch} : r))
    );
  };

  const draftSelfBookingReminderOffsetsCommit = useMemo(
    () => computeSelfBookingReminderOffsetsCommit(draftSelfBookingReminderOffsetRows),
    [draftSelfBookingReminderOffsetRows]
  );

  const openReminderHoursModal = () => {
    setDraftReminderHourInputs(
      participantReminderHours.map((h) => makeReminderHourSlot(String(h)))
    );
    setReminderHoursModalOpen(true);
  };

  const handleReminderHoursModalSave = () => {
    if (draftReminderHoursCommit.hasDuplicates) {
      return;
    }
    if (
      draftReminderHoursCommit.valid &&
      draftReminderHoursCommit.hasAtLeastOne &&
      areParticipantReminderHoursEqual(
        draftReminderHoursCommit.hours,
        participantReminderHours
      )
    ) {
      return;
    }
    if (!draftReminderHoursCommit.valid) {
      toast({
        title: 'Invalid hours',
        description: `Enter whole hours between 1 and ${MAX_REMINDER_HOURS_VALUE} for each reminder.`,
        variant: 'destructive',
      });
      return;
    }
    if (!draftReminderHoursCommit.hasAtLeastOne) {
      toast({
        title: 'At least one reminder',
        description:
          'Keep at least one reminder time, or add hours in the empty fields before saving.',
        variant: 'destructive',
      });
      return;
    }
    setParticipantReminderHours(draftReminderHoursCommit.hours);
    setReminderHoursModalOpen(false);
  };

  const handleReminderHoursModalCancel = () => {
    setReminderHoursModalOpen(false);
  };

  const addDraftReminderSlot = () => {
    if (draftReminderHourInputs.length >= MAX_PARTICIPANT_REMINDER_SLOTS) return;
    setDraftReminderHourInputs((prev) => [...prev, makeReminderHourSlot('')]);
  };

  const removeDraftReminderSlot = (index: number) => {
    if (draftReminderHourInputs.length <= MIN_PARTICIPANT_REMINDER_SLOTS) return;
    setDraftReminderHourInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDraftReminderSlot = (index: number, value: string) => {
    setDraftReminderHourInputs((prev) => {
      const copy = [...prev];
      copy[index] = {...copy[index], value};
      return copy;
    });
  };

  const draftReminderHoursCommit = useMemo(
    () => computeReminderHoursCommit(draftReminderHourInputs),
    [draftReminderHourInputs]
  );

  const reminderModalDraftMatchesSaved =
    draftReminderHoursCommit.valid &&
    !draftReminderHoursCommit.hasDuplicates &&
    draftReminderHoursCommit.hasAtLeastOne &&
    areParticipantReminderHoursEqual(
      draftReminderHoursCommit.hours,
      participantReminderHours
    );

  const reminderModalSaveDisabled =
    !draftReminderHoursCommit.valid ||
    !draftReminderHoursCommit.hasAtLeastOne ||
    reminderModalDraftMatchesSaved;

  const selfBookingReminderModalDraftMatchesSaved =
    draftSelfBookingReminderOffsetsCommit.valid &&
    !draftSelfBookingReminderOffsetsCommit.hasDuplicates &&
    areSelfBookingReminderOffsetsEqual(
      draftSelfBookingReminderOffsetsCommit.offsets,
      selfBookingReminderOffsetsCommit
    );

  const selfBookingReminderModalSaveDisabled =
    !draftSelfBookingReminderOffsetsCommit.valid ||
    selfBookingReminderModalDraftMatchesSaved;

  return (
    <div className="space-y-6">
      {!embedded && currentProject && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Home className="h-4 w-4 shrink-0" />
          <span>One Data</span>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span>Projects</span>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span>{currentProject.name}</span>
          <ChevronRight className="h-4 w-4 shrink-0" />
          <span className="text-foreground font-medium">Email Setup</span>
        </div>
      )}

      <div className="space-y-1">
        {!embedded && (
          <h1 className="text-3xl font-bold text-foreground">Email Setup</h1>
        )}
        <p className="text-sm text-muted-foreground">
          These templates control the emails sent to{' '}
          <strong>participants</strong>. Moderator notifications use system
          defaults and are not configurable here.
        </p>
      </div>

      <div
        role="status"
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
          <div className="space-y-1">
            <h5 className="font-medium leading-none tracking-tight">
              Configure all email templates before booking sessions
            </h5>
            <p className="text-sm">
              This helps ensure a smooth scheduling process and avoids
              interruptions when performing actions such as booking,
              rescheduling, cancellations, reminders, and session status
              updates.
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="templates" className="space-y-6">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6">
      <div className="rounded-md border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Bulk upload templates (POC)</h2>
            <p className="text-xs text-muted-foreground">
              Download JSON template, fill all 10 template slots, then upload.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadBulkTemplate}
              disabled={saving || bulkImporting}
            >
              <Download className="h-4 w-4 mr-1" />
              Download template
            </Button>
            <Button
              type="button"
              onClick={() => bulkUploadInputRef.current?.click()}
              disabled={!canEdit || saving || bulkImporting}
            >
              <Upload className="h-4 w-4 mr-1" />
              {bulkImporting ? 'Importing...' : 'Upload JSON'}
            </Button>
            <input
              ref={bulkUploadInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleBulkUploadFile}
            />
          </div>
        </div>
        <BulkUploadReportPanel report={bulkUploadReport} />
      </div>

      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Status: <strong>{configuredTemplateCount}/{TEMPLATE_TABS.length}</strong>{' '}
          templates configured
        </p>
        <div className="inline-flex min-h-9 flex-wrap items-center justify-start gap-1">
          {TEMPLATE_TABS.map((tab) => {
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 h-9 rounded-md border px-4 text-sm font-medium transition-colors',
                  active
                    ? 'border-slate-900 bg-slate-900 text-white shadow-none'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                )}
              >
                {templateValidityByKey[tab.id] ? (
                  <CircleCheck
                    className="size-4 text-emerald-500"
                    aria-hidden="true"
                  />
                ) : (
                  <span className="size-2 rounded-full bg-muted-foreground/40" />
                )}
                {tab.label}
              </button>
            );
          })}
        </div>
        <p className="text-sm text-muted-foreground">
          {activeTab === 'reminder' && reminderFeatureEnabled
            ? 'Sent to participant at each configured time before the session.'
            : TAB_HELP_TEXT[activeTab]}
        </p>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-9">
            {activeTab === 'reminder' && reminderFeatureEnabled && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50/80 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    Reminder schedule
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatReminderHoursSummary(participantReminderHours)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canEdit || saving || bulkImporting}
                  onClick={openReminderHoursModal}
                >
                  Configure hours
                </Button>
              </div>
            )}
            {activeTab === 'self_booking_reminder' && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50/80 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    Reminder schedule
                  </p>
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="self-booking-reminder-preview"
                  >
                    {formatSelfBookingReminderPreview(selfBookingReminderOffsetsCommit)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canEdit || saving || bulkImporting}
                  onClick={openSelfBookingReminderHoursModal}
                >
                  Configure hours
                </Button>
              </div>
            )}
            <div className="rounded-md border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-sm font-medium">Email Subject</label>
              {activeTab === 'moderator_change' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canEdit || saving || bulkImporting}
                  onClick={() =>
                    updateActive({
                      subject: DEFAULT_MODERATOR_CHANGE_SUBJECT,
                      body: DEFAULT_MODERATOR_CHANGE_BODY,
                    })
                  }
                >
                  Use default copy
                </Button>
              )}
            </div>
            <div className="relative mt-1">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-md border border-transparent px-4 py-3 text-base text-slate-700 md:text-sm"
              >
                <div
                  className="flex min-h-[1.5rem] items-center whitespace-pre leading-normal"
                  style={{
                    transform: `translateX(${-subjectScrollLeft}px)`,
                  }}
                >
                  {renderHighlightedTokens(
                    activeConfig.subject,
                    activeMandatoryPlaceholders
                  )}
                </div>
              </div>
              <Input
                ref={subjectRef}
                value={activeConfig.subject}
                onChange={(e) => updateActive({subject: e.target.value})}
                onFocus={() => setActiveEditorField('subject')}
                onKeyDown={handleSubjectKeyDown}
                onScroll={(e) =>
                  setSubjectScrollLeft(e.currentTarget.scrollLeft)
                }
                placeholder={SUBJECT_PLACEHOLDER_TEXT[activeTab]}
                disabled={!canEdit || saving || bulkImporting}
                style={
                  activeConfig.subject
                    ? {
                        color: 'transparent',
                        WebkitTextFillColor: 'transparent',
                      }
                    : undefined
                }
                className="relative z-10 !h-12 !min-h-12 !border-input !bg-transparent !px-4 !py-3 caret-foreground"
              />
            </div>
            </div>
            <div className="rounded-md border border-slate-200 p-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium">Email Body</label>
              </div>
              <EmailEditor
                ref={emailEditorRef}
                value={activeConfig.body}
                onChange={(html) => updateActive({body: html})}
                onFocus={() => setActiveEditorField('body')}
                placeholder={BODY_PLACEHOLDER_TEXT[activeTab]}
                disabled={!canEdit || saving || bulkImporting}
              />
            </div>
          </div>
          <div className="rounded-md border border-slate-200 p-4 lg:col-span-3">
            <p className="text-sm font-medium">Placeholders</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Click a token to insert.
            </p>
            <div className="mt-3 space-y-2">
              {activePlaceholders.map((token) => {
                const isMandatory = activeMandatoryPlaceholders.has(token);
                return (
                  <button
                    key={token}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertPlaceholder(token)}
                    disabled={!canEdit || saving || bulkImporting}
                    className="w-full rounded-md border p-2 text-left hover:bg-slate-50 disabled:opacity-60"
                  >
                    <code
                      className={isMandatory ? 'text-xs font-bold' : 'text-xs'}
                    >
                      {token}
                    </code>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleReset} disabled={saving || bulkImporting || !hasChanges}>
              Reset
            </Button>
            <Tooltip open={missingMandatoryTokens.length > 0 && saveTooltipOpen}>
              <TooltipTrigger asChild>
                <span
                  tabIndex={missingMandatoryTokens.length > 0 ? 0 : -1}
                  onMouseEnter={() => setSaveTooltipOpen(true)}
                  onMouseLeave={() => setSaveTooltipOpen(false)}
                  onFocus={() => setSaveTooltipOpen(true)}
                  onBlur={() => setSaveTooltipOpen(false)}
                >
                  <Button
                    onClick={handleSave}
                    disabled={saving || bulkImporting || !canSaveEmailSetup}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </span>
              </TooltipTrigger>
              {missingMandatoryTokens.length > 0 && (
                <TooltipContent>
                  {missingMandatoryTokens.join(' and ')}{' '}
                  {missingMandatoryTokens.length > 1 ? 'are' : 'is'} required in
                  the email body before you can save.
                </TooltipContent>
              )}
            </Tooltip>
        </div>
      </div>

      <Dialog
        open={reminderHoursModalOpen}
        onOpenChange={(open) => {
          if (!open) handleReminderHoursModalCancel();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configure reminder hours</DialogTitle>
            <DialogDescription>
              Minimum 1 reminder, maximum 3 reminders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {draftReminderHoursCommit.hasDuplicates && (
              <p className="text-sm text-destructive" role="alert">
                There are duplicate reminder times. Use a different number of
                hours for each row.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Hours before session start
            </p>
            {draftReminderHourInputs.map((slot, index) => (
              <div key={slot.id} className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={MAX_REMINDER_HOURS_VALUE}
                  inputMode="numeric"
                  value={slot.value}
                  onChange={(e) =>
                    updateDraftReminderSlot(index, e.target.value)
                  }
                  disabled={!canEdit}
                  className="flex-1"
                  aria-label={`Reminder ${index + 1} hours before session`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-destructive hover:text-destructive"
                  disabled={
                    !canEdit ||
                    draftReminderHourInputs.length <=
                      MIN_PARTICIPANT_REMINDER_SLOTS
                  }
                  onClick={() => removeDraftReminderSlot(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            {draftReminderHourInputs.length < MAX_PARTICIPANT_REMINDER_SLOTS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!canEdit}
                onClick={addDraftReminderSlot}
              >
                + Add another reminder
              </Button>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleReminderHoursModalCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleReminderHoursModalSave}
              disabled={!canEdit || reminderModalSaveDisabled}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selfBookingReminderHoursModalOpen}
        onOpenChange={(open) => {
          if (!open) handleSelfBookingReminderHoursModalCancel();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configure reminder hours</DialogTitle>
            <DialogDescription>
              Up to 3 reminders. Remove all rows to turn reminders off.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {draftSelfBookingReminderOffsetsCommit.hasDuplicates && (
              <p className="text-sm text-destructive" role="alert">
                There are duplicate reminder times. Use a different number of
                hours or type for each row.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Hours after invite sent, or before the invitation expires
            </p>
            {draftSelfBookingReminderOffsetRows.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={MAX_SELF_BOOKING_REMINDER_HOURS}
                  inputMode="numeric"
                  value={row.hours}
                  onChange={(e) =>
                    updateDraftSelfBookingReminderOffsetRow(row.id, {
                      hours: e.target.value,
                    })
                  }
                  disabled={!canEdit}
                  className="flex-1"
                  aria-label={`Reminder ${index + 1} hours`}
                />
                <Select
                  value={row.type}
                  onValueChange={(value) =>
                    updateDraftSelfBookingReminderOffsetRow(row.id, {
                      type: value as SelfBookingReminderOffsetType,
                    })
                  }
                  disabled={!canEdit}
                >
                  <SelectTrigger className="w-56 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="after_sent">Hours after invite sent</SelectItem>
                    <SelectItem value="before_expiry">
                      Hours before invitation expires
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-destructive hover:text-destructive"
                  disabled={!canEdit}
                  onClick={() => removeDraftSelfBookingReminderOffsetRow(row.id)}
                >
                  Remove
                </Button>
              </div>
            ))}
            {draftSelfBookingReminderOffsetRows.length < MAX_SELF_BOOKING_REMINDER_SLOTS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!canEdit}
                onClick={addDraftSelfBookingReminderOffsetRow}
              >
                + Add another reminder
              </Button>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleSelfBookingReminderHoursModalCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSelfBookingReminderHoursModalSave}
              disabled={!canEdit || selfBookingReminderModalSaveDisabled}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>

        <TabsContent value="history">
          <EmailTemplateHistoryPanel projectId={currentProject?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
